import { useState, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../App";
import { formatDate as formatLocaleDate } from "../../i18n";
import { 
  Calendar, 
  ChevronRight, 
  AlertCircle,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

const DeadlinesPanel = memo(() => {
  const { t, i18n } = useTranslation();
  const [monthOffset, setMonthOffset] = useState(0);
  const [deadlines, setDeadlines] = useState([]);
  const [monthLabel, setMonthLabel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeadlines();
  }, [monthOffset]);

  const fetchDeadlines = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/dashboard/deadlines?month_offset=${monthOffset}`);
      setDeadlines(response.data.deadlines);
      setMonthLabel(response.data.month_label);
    } catch (error) {
      console.error("Failed to load deadlines:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return formatLocaleDate(dateStr, i18n.language);
  };

  const filterLabels = [
    { offset: 0, label: t("today.deadlines.thisMonth") },
    { offset: 1, label: t("today.deadlines.nextMonth") },
    { offset: 2, label: t("today.deadlines.monthAfter") }
  ];

  return (
    <Card className="border border-border" data-testid="deadlines-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t("today.deadlines.title")}
            </CardTitle>
            {!loading && (
              <Badge variant="secondary" className="rounded-full text-xs">
                {deadlines.length}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Month Filter Buttons */}
        <div className="flex gap-2 mt-3">
          {filterLabels.map((f) => (
            <button
              key={f.offset}
              onClick={() => setMonthOffset(f.offset)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                monthOffset === f.offset 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`deadline-filter-${f.offset}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : deadlines.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">{t("today.deadlines.noDeadlines", { month: monthLabel })}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-3">{monthLabel}</p>
            {deadlines.map((deadline) => (
              <Link
                key={deadline.id}
                to={`/programs/${deadline.program_id}?tab=overview`}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                data-testid={`deadline-${deadline.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{deadline.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {deadline.program_name}
                    {deadline.pm_name && ` · ${deadline.pm_name}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    {formatDate(deadline.due_date)}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

DeadlinesPanel.displayName = 'DeadlinesPanel';

export default DeadlinesPanel;
